import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import PageLoader from 'components/page-loader/page-loader';
import { useCurrentUserQuery } from 'hooks/useCurrentUserQuery';
import type { UserEntity } from 'services/entities/UserEntity';
import type { UserRepository } from 'services/repositories/interfaces/UserRepository';

type UserContextResult = Pick<
  ReturnType<typeof useCurrentUserQuery>,
  'data' | 'error' | 'isError' | 'isLoading' | 'isLoadingError' | 'isRefetchError' | 'isSuccess' | 'status'
>;

const UserContextPageLoading = ({ children }: { children: ReactNode }) => {
  const { isLoading, data } = useUserContext();
  return (
    <PageLoader isLoading={isLoading} isLoaded={Boolean(data)}>
      {children}
    </PageLoader>
  );
};

type UserContextValue = UserContextResult | null;
const UserContext = createContext<UserContextValue>(null);
UserContext.displayName = 'UserContext';

interface Props {
  children: ReactNode;
  userRepository: UserRepository;
}

export const UserContextProvider = ({ children, userRepository }: Props) => {
  const queryResult = useCurrentUserQuery(userRepository, { enabled: true });

  return <UserContext.Provider value={queryResult}>{children}</UserContext.Provider>;
};

/**
 * Provides the UserContext from an already-fetched user (e.g. from MFE injectables).
 * Avoids a duplicate GET /users/me when the host has already authenticated and passes
 * the user via the injectable bridge.
 */
export const InjectableUserProvider = ({ children, user }: { children: ReactNode; user: UserEntity }) => {
  const value: UserContextResult = {
    data: user,
    error: null,
    isError: false,
    isLoading: false,
    isLoadingError: false,
    isRefetchError: false,
    isSuccess: true,
    status: 'success',
  };
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

const UserProvider = (props: Props) => {
  return (
    <UserContextProvider {...props}>
      <UserContextPageLoading>{props.children}</UserContextPageLoading>
    </UserContextProvider>
  );
};

export const useUserContext = () => {
  const value = useContext(UserContext);
  if (!value) {
    throw Error('useUserContext should be used inside  UserProvider');
  }

  return value;
};

export const useCurrentUser = () => {
  const { data, isLoading } = useUserContext();
  if (!data) {
    throw Error('user is missing. You need to load data in UserContext provider before using useCurrentUser hook.');
  }

  return { data, isLoading };
};

export default UserProvider;
